import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

import { i18n } from "../../translate/i18n";
import AerosDialog from "../AerosDialog";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";

const EXAMPLE_VARIABLES = ["nome", "email", "telefone", "cidade"];

const useStyles = makeStyles((theme) => ({
  chips: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 },
  chip: {
    background: "rgba(34,211,238,0.12)",
    color: stateText(theme, AEROS.cyan, "info"),
    border: "none",
    cursor: "pointer",
    fontFamily: "inherit",
    "&:hover": { background: "rgba(34,211,238,0.2)" },
  },
  infoText: { fontSize: 11.5, lineHeight: 1.5, color: theme.palette.text.secondary },
}));

const FlowBuilderInputModal = ({ open, onSave, onUpdate, data, close }) => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const isMounted = useRef(true);
  const [activeModal, setActiveModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [question, setQuestion] = useState("");
  const [variableName, setVariableName] = useState("");
  const questionInputRef = useRef(null);

  useEffect(() => {
    if (open === "edit") {
      setQuestion(data.data.question || "");
      setVariableName(data.data.variableName || "");
      setActiveModal(true);
    } else if (open === "create") {
      setQuestion("");
      setVariableName("");
      setActiveModal(true);
    } else {
      setActiveModal(false);
    }
  }, [open, data]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
    setLoading(false);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      if (open === "edit") {
        handleClose();
        onUpdate({
          ...data,
          data: {
            question: question,
            variableName: variableName,
          },
        });
      } else if (open === "create") {
        handleClose();
        onSave({
          question: question,
          variableName: variableName,
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const handleVariableClick = (name) => {
    if (questionInputRef.current) {
      const inputElement = questionInputRef.current.querySelector("textarea");
      if (inputElement) {
        const start = inputElement.selectionStart;
        const end = inputElement.selectionEnd;
        const variableText = `\${${name}}`;
        const newText = question.substring(0, start) + variableText + question.substring(end);
        setQuestion(newText);
        const newCursorPosition = start + variableText.length;
        setTimeout(() => {
          inputElement.focus();
          inputElement.setSelectionRange(newCursorPosition, newCursorPosition);
        }, 100);
      }
    }
  };

  const isFormValid = () => {
    return question.trim() !== "" && variableName.trim() !== "";
  };

  return (
    <AerosDialog
      open={activeModal}
      onClose={handleClose}
      width={560}
      title={i18n.t(open === "edit" ? "aeros.flow.modals.input.editTitle" : "aeros.flow.modals.input.addTitle")}
      onSubmit={handleSave}
      submitLabel={i18n.t(open === "edit" ? "aeros.flow.save" : "aeros.flow.add")}
      submitDisabled={!isFormValid()}
      loading={loading}
    >
      <div>
        <label className={m.fieldLabel} htmlFor="flow-input-question">
          {i18n.t("aeros.flow.modals.input.question")}
        </label>
        <TextField
          id="flow-input-question"
          className={m.input}
          variant="outlined"
          multiline
          rows={4}
          name="question"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          ref={questionInputRef}
          placeholder={i18n.t("aeros.flow.modals.input.questionPlaceholder")}
        />
        <div className={m.hint}>{i18n.t("aeros.flow.modals.input.questionHint")}</div>
        <div className={classes.chips}>
          {EXAMPLE_VARIABLES.map((name) => (
            <button
              type="button"
              key={name}
              className={`${m.chip} ${classes.chip}`}
              onClick={() => handleVariableClick(name)}
            >
              {"${" + name + "}"}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={m.fieldLabel} htmlFor="flow-input-variable">
          {i18n.t("aeros.flow.modals.input.variableName")}
        </label>
        <TextField
          id="flow-input-variable"
          className={m.input}
          variant="outlined"
          name="variableName"
          value={variableName}
          onChange={(e) => setVariableName(e.target.value)}
          placeholder={i18n.t("aeros.flow.modals.input.variablePlaceholder")}
        />
        <div className={m.hint}>{i18n.t("aeros.flow.modals.input.variableHint")}</div>
      </div>
      <div className={m.optionBox}>
        <InfoOutlinedIcon />
        <div style={{ minWidth: 0 }}>
          <div className={m.optionTitle}>{i18n.t("aeros.flow.modals.input.howItWorks")}</div>
          <div className={classes.infoText}>{i18n.t("aeros.flow.modals.input.howItWorksText")}</div>
        </div>
      </div>
    </AerosDialog>
  );
};

export default FlowBuilderInputModal;
